import { useState, useEffect, MouseEvent } from "react";
import {
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Checkbox,
} from "@mui/material";
import { CheckCircle, Circle } from "@mui/icons-material";
import { CallSheetParticipantInput } from "../../../../../../graphql/generated/graphql";
import TimeTool from "../../../../components/time-tool";
import { useCallSheetState } from "../../../../../hooks/callSheet";
import { useParams } from "react-router-dom";
import { formatTime } from "../../../../../helper/converters";
import { Data, Order } from "./interfaces";
import EnhancedTableToolbar from "./EnhancedTableToolbar";
import EnhancedTableHead from "./EnhancedTableHead";

function createData(
  id: string,
  name: string,
  jobTitle: string,
  email: string,
  phone: string,
  time: any
): Data {
  return {
    id,
    name,
    jobTitle,
    email,
    phone,
    time,
  };
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(
  array: readonly T[],
  comparator: (a: T, b: T) => number
) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const CrewsTable = (props: any) => {
  const params = useParams();
  const { idCallsheet } = params;
  const callSheetState = useCallSheetState();
  const {
    name: crewName,
    title,
    users,
    time,
    selected: selectedValue,
    sendSelected,
    assigned,
  } = props;
  const [flag, setFlag] = useState<boolean>(false);
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof Data>("name");
  const [selected, setSelected] = useState<
    readonly CallSheetParticipantInput[]
  >([]);
  const rows: Data[] = [];
  const participantsStore = callSheetState.assignedByCategory.get();

  const participantsItems = participantsStore?.filter(
    (item) => item.name === crewName
  )[0];

  users.forEach((element: any) => {
    let timeItem = time;
    if (participantsItems) {
      const user = participantsItems?.users.find(
        (ele: any) => ele.email === element.email
      );
      if (user) timeItem = formatTime(user.time);
    }
    rows.push(
      createData(
        element.id || "",
        element.full_name || "",
        element.job_title?.name || "",
        element.email || "",
        element.phone || "",
        timeItem
      )
    );
  });

  const handleSelectAllClick = (value: boolean, skip = false) => {
    if (value) {
      if (!idCallsheet) {
        const newSelecteds = rows.map((n) => ({
          email: n.email,
          id: n.id,
          time: n.time,
        }));
        setSelected(newSelecteds);
        sendSelected(newSelecteds);
      } else {
        if (skip || flag) {
          const newSelecteds = rows.map((n) => ({
            email: n.email,
            id: n.id,
            time: n.time,
          }));
          setSelected(newSelecteds);
          sendSelected(newSelecteds);
        }
      }
      return;
    }
    sendSelected(selected, true);
    setSelected([]);
  };

  const handleSelectAllClickHeader = (value: boolean) => {
    if (idCallsheet) {
      if (!flag) {
        setFlag(true);
        handleSelectAllClick(value, true);
      } else {
        handleSelectAllClick(value);
      }
    } else {
      handleSelectAllClick(value);
    }
  };

  useEffect(() => {
    handleSelectAllClick(selectedValue);
  }, [selectedValue]);

  useEffect(() => {
    if (assigned && selected.length === 0) {
      assigned.map((ele: any) => {
        if (ele.name === crewName) {
          const usersArray = ele.users
            .filter((e: any) => e.assigned_in_call_sheet)
            .map((usr: any) => {
              const person = rows.find((user) => user.email === usr.email);
              return {
                email: usr.email,
                id: person?.id,
                time: formatTime(usr.time_call_sheet),
              };
            });
          setSelected(usersArray);
          sendSelected(usersArray, false, true);
        }
      });
    }
  }, [assigned]);

  const handleRequestSort = (
    event: MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleClick = (event: any, selectRow: CallSheetParticipantInput) => {
    if (idCallsheet) setFlag(true);
    const selectedIndex = selected.findIndex((x) => x.id === selectRow.id);
    let newSelected: readonly CallSheetParticipantInput[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, selectRow);
      sendSelected([selectRow], false);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
      sendSelected([selectRow], true);
    } else if (selectedIndex === selected.length - 1) {
      sendSelected([selectRow], true);
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      sendSelected([selectRow], true);
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const isSelected = (email: string) => {
    if (idCallsheet && !flag) {
      if (participantsItems) {
        const user = participantsItems?.users.find(
          (ele: any) => ele.email === email
        );
        return Boolean(user);
      } else return false;
    } else return selected.findIndex((x) => x.email === email) !== -1;
  };

  const renderStatus = (email: string) => {
    if (idCallsheet) {
      const Participants = callSheetState.participantByCategory.get();
      const isActive: any = Boolean(
        Participants?.filter((item) => item.name === crewName)[0]
          ?.users.map((e: any) => e.email)
          .find((ele: any) => ele.includes(email))
      );
      return isActive ? (
        <CheckCircle className="active-participant" />
      ) : (
        <Circle className="inactive-participant" />
      );
    }
    return;
  };

  const checkingTime = (filter: any, rowTime: any) => (filter ? rowTime : time);

  return (
    <>
      {rows.length > 0 && (
        <Box
          sx={{
            width: 550,
            maxWidth: "100%",
            my: 2,
          }}
          className={`crew-table-wrapper ${title ? "full-width" : ""}`}
        >
          {title && (
            <div className="crew-table-wrapper-subtitle">
              <h2>{title}</h2>
            </div>
          )}
          <TableContainer
            component={Paper}
            className="crew-table"
          >
            <EnhancedTableToolbar
              nameCrew={crewName}
              title={title}
            />
            <Table
              sx={{ width: "100%" }}
              aria-labelledby="tableTitle"
              className="crew-table-body"
            >
              <EnhancedTableHead
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClickHeader}
                onRequestSort={handleRequestSort}
                rowCount={rows.length}
              />
              <TableBody>
                {stableSort(rows, getComparator(order, orderBy)).map(
                  (row, index) => {
                    const isItemSelected = isSelected(row.email as string);
                    const labelId = `enhanced-table-checkbox-${index}`;
                    const defaultValue = checkingTime(isItemSelected, row.time);
                    return (
                      <TableRow
                        hover
                        tabIndex={-1}
                        key={row.id}
                        selected={isItemSelected}
                      >
                        <TableCell
                          component="td"
                          id={labelId}
                          scope="row"
                          padding="none"
                        >
                          <Grid
                            sx={{
                              display: "flex",
                              alignItems: "start",
                              flexDirection: "column",
                            }}
                          >
                            <p>{row.name}</p>
                            <p>{row.jobTitle}</p>
                          </Grid>
                        </TableCell>
                        <TableCell align="left" width='30%'>
                          <Grid
                            sx={{
                              display: "flex",
                              alignItems: "start",
                              flexDirection: "column",
                            }}
                          >
                            <p><abbr title={row.email}>{row.email}</abbr></p>
                            <p>{row.phone}</p>
                          </Grid>
                        </TableCell>
                        <TableCell align="left">
                          <TimeTool
                            size="small"
                            disabled
                            time={defaultValue}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  }
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </>
  );
};

export default CrewsTable;
